/**
 * Shared click-handler for the "Manage access" menu item that appears
 * in every artifact / attachment / collection kebab menu.
 *
 * Three branches, in priority order:
 *
 *   1. **Tier denies privacyControls** → fire the
 *      registry-driven upgrade gate. The user lands in the standard
 *      tier-feature upgrade modal with copy pulled from the
 *      privacyControls entry in the feature registry.
 *
 *   2. **TEAM tier but operator hasn't enabled ACL** (per-workspace
 *      `aclEnforceReads` / `aclEnforceWrites` flags) → show a one-off
 *      toast pointing the admin at the workspace-settings rollout.
 *      Distinct from the upsell because the gate is operator-side, not
 *      billing-side.
 *
 *   3. **TEAM + ACL on** → call `openDialog()` which opens the existing
 *      ShareEntityDialog.
 *
 * The menu items themselves are always visible (no longer hidden when
 * `aclEnforced` is false).
 *
 * NOTE (2026-07-26): `privacyControls` is now TRUE on every tier, so branch 1
 * is currently unreachable. It is kept because the gate is tier-driven data,
 * not a constant — re-gating the feature must not also require rebuilding the
 * upsell path.
 */

import { useAppToast } from './useAppToast';
import { useCanUseFeature } from './useFeatureGate';
import { useIsAclEnforced } from '../data/useEntityAccess';
import { emitUpgradeGateBlocked } from '../../services/events/upgradeGateEvents';

export interface ManagePermissionsAction {
  /** Wrapped onClick — call from the menu item. */
  readonly onClick: () => void;
  /**
   * True only when the tier includes `privacyControls`. Used by callers
   * that want to disable / hide a peer button (the dialog renderer
   * still needs `aclEnforced` to be true).
   */
  readonly tierAllowsManagement: boolean;
  /**
   * True when the tier allows AND the operator has enabled ACL —
   * the only state where opening the dialog is meaningful.
   */
  readonly canOpenDialog: boolean;
}

export function useManagePermissionsAction(args: {
  openDialog: () => void;
}): ManagePermissionsAction {
  // track: false — the click handler fires emitUpgradeGateBlocked which is the real intent signal
  const { allowed: tierAllowsManagement } = useCanUseFeature('privacyControls', { track: false });
  const aclEnforced = useIsAclEnforced();
  const toast = useAppToast();

  const canOpenDialog = tierAllowsManagement && aclEnforced;

  const onClick = () => {
    if (!tierAllowsManagement) {
      // Registry-driven upgrade modal — `tier-feature` GateConfig reads
      // copy from FEATURE_REGISTRY.privacyControls.
      emitUpgradeGateBlocked('tier-feature', {
        featureKey: 'privacyControls',
      });
      return;
    }
    if (!aclEnforced) {
      toast({
        title: 'Per-artifact access controls aren’t enabled yet',
        description:
          'Your workspace admin needs to enable entity-level access in Workspace settings before per-artifact permissions can be managed here.',
        status: 'info',
        duration: 6000,
        isClosable: true,
      });
      return;
    }
    args.openDialog();
  };

  return { onClick, tierAllowsManagement, canOpenDialog };
}
